import { useEffect, useState } from 'react';
import {
    Badge, Group, Button, Menu, ActionIcon, Text, Stack, Alert, Modal, TextInput, Textarea, NumberInput, MultiSelect,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { MoreVertical, Plus, AlertTriangle, RefreshCw } from 'lucide-react';
import {
    FEATURE_CATALOG, ALL_METERING_CATEGORIES, METERING_CATEGORY_META,
    type FeatureKey, type MeteringCategory, type PlanQuota, type PlanSummary,
} from '@bmai/shared';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorPlansViewModel } from './OperatorPlansViewModel';
import type { PlanUpsert } from './OperatorPlansService';

function priceLabel(p: number | null): string {
    return p === null ? 'Custom' : `$${p.toLocaleString()}`;
}

function quotaLabel(q: number | null | undefined): string {
    return q === null || q === undefined ? '∞' : q.toLocaleString();
}

async function run(p: Promise<void>, successMsg: string): Promise<void> {
    try {
        await p;
        notifications.show({ color: 'green', message: successMsg });
    } catch (e) {
        notifications.show({ color: 'red', title: 'Action failed', message: e instanceof Error ? e.message : 'Unexpected error' });
    }
}

export default function OperatorPlansPage(): React.ReactElement {
    const vm = useViewModel(OperatorPlansViewModel);
    const [editTarget, setEditTarget] = useState<PlanSummary | null | undefined>(undefined); // undefined = closed, null = new

    useEffect(() => { void vm.load(); }, [vm]);

    const columns: MegaGridColumn<PlanSummary>[] = [
        {
            key: 'name', header: 'Plan', sortValue: (p) => p.displayName,
            render: (p) => (
                <div>
                    <Text size="sm" fw={500}>{p.displayName}</Text>
                    <Text size="xs" c="dimmed">{p.name}</Text>
                </div>
            ),
        },
        { key: 'price', header: 'Price', align: 'right', sortValue: (p) => p.priceMonthly ?? Number.MAX_SAFE_INTEGER, render: (p) => priceLabel(p.priceMonthly) },
        { key: 'seats', header: 'Seats', align: 'right', sortValue: (p) => p.quotas.seats ?? Number.MAX_SAFE_INTEGER, render: (p) => quotaLabel(p.quotas.seats) },
        { key: 'tokens', header: 'LLM tokens', align: 'right', sortValue: (p) => p.quotas.llm_tokens ?? Number.MAX_SAFE_INTEGER, render: (p) => quotaLabel(p.quotas.llm_tokens) },
        { key: 'features', header: 'Features', sortValue: (p) => p.features.length, render: (p) => <Badge variant="light" size="sm">{p.features.length} features</Badge> },
        {
            key: 'actions', header: '', width: 56, align: 'right', noFilter: true,
            render: (p): React.ReactElement => (
                <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Actions"><MoreVertical size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item onClick={() => setEditTarget(p)}>Edit</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={() => void run(vm.remove(p.id), 'Plan deleted')}>Delete</Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            ),
        },
    ];

    return (
        <Stack gap="md">
            <PageHeaderComponent
                title="Plans"
                subtitle="Reusable plans (features + quota buckets) you assign to tenants."
                rightToolbar={
                    <Group gap="xs">
                        <Button variant="light" leftSection={<RefreshCw size={16} />} onClick={() => void vm.load()}>Refresh</Button>
                        <Button leftSection={<Plus size={16} />} onClick={() => setEditTarget(null)}>New plan</Button>
                    </Group>
                }
            />

            {vm.error && <Alert color="red" icon={<AlertTriangle size={16} />}>{vm.error}</Alert>}

            <MegaGrid
                persistenceKey="operator-plans"
                columns={columns}
                rows={vm.plans ?? []}
                getRowKey={(p) => p.id}
                searchAccessor={(p) => `${p.name} ${p.displayName}`}
                searchPlaceholder="Search plans"
                emptyMessage="No plans"
                loading={vm.loading}
            />

            <PlanEditor target={editTarget} onClose={() => setEditTarget(undefined)} vm={vm} />
        </Stack>
    );
}

function PlanEditor({ target, onClose, vm }: { target: PlanSummary | null | undefined; onClose: () => void; vm: OperatorPlansViewModel }): React.ReactElement {
    const [name, setName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState<number | ''>('');
    const [features, setFeatures] = useState<string[]>([]);
    const [quotas, setQuotas] = useState<Record<string, number | ''>>({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (target === undefined) return;
        setName(target?.name ?? '');
        setDisplayName(target?.displayName ?? '');
        setDescription(target?.description ?? '');
        setPrice(target?.priceMonthly ?? '');
        setFeatures(target?.features ?? []);
        const q: Record<string, number | ''> = {};
        for (const cat of ALL_METERING_CATEGORIES) {
            const v = target?.quotas?.[cat];
            q[cat] = v === null || v === undefined ? '' : v;
        }
        setQuotas(q);
    }, [target]);

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            const q: PlanQuota = {};
            for (const cat of ALL_METERING_CATEGORIES) {
                q[cat] = quotas[cat] === '' ? null : Number(quotas[cat]);
            }
            const input: PlanUpsert = {
                name: name.trim(),
                displayName: displayName.trim() || name.trim(),
                ...(description.trim() ? { description: description.trim() } : {}),
                features: features as FeatureKey[],
                quotas: q,
                priceMonthly: price === '' ? null : Number(price),
            };
            await vm.save(target?.id ?? null, input);
            notifications.show({ color: 'green', message: `Saved ${input.name}` });
            onClose();
        } catch (e) {
            notifications.show({ color: 'red', title: 'Save failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={target !== undefined} onClose={onClose} title={target ? `Edit plan — ${target.displayName}` : 'New plan'} centered>
            <Stack gap="sm">
                <Group grow>
                    <TextInput label="Key" placeholder="team" value={name} onChange={(e) => setName(e.currentTarget.value)} disabled={!!target} required />
                    <TextInput label="Display name" placeholder="Team" value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} />
                </Group>
                <Textarea label="Description" value={description} onChange={(e) => setDescription(e.currentTarget.value)} autosize minRows={2} />
                <NumberInput label="Monthly price (USD)" description="Leave blank for custom/unpriced" value={price} onChange={(v) => setPrice(typeof v === 'number' ? v : '')} min={0} prefix="$" thousandSeparator="," />
                <MultiSelect
                    label="Features"
                    description="Data & analytics features drive usage ingestion + classification. IdP + LLM connections are always-on core."
                    data={[
                        { group: 'Data & analytics (drives classification)', items: FEATURE_CATALOG.filter((f) => f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
                        { group: 'Platform', items: FEATURE_CATALOG.filter((f) => !f.requiresIngestion).map((f) => ({ value: f.key, label: f.label })) },
                    ]}
                    value={features}
                    onChange={setFeatures}
                    searchable
                />
                {ALL_METERING_CATEGORIES.map((cat: MeteringCategory) => (
                    <NumberInput
                        key={cat}
                        label={`${METERING_CATEGORY_META[cat].label} quota`}
                        description={`Blank = unlimited (${METERING_CATEGORY_META[cat].unit})`}
                        value={quotas[cat] ?? ''}
                        onChange={(v) => setQuotas((q) => ({ ...q, [cat]: typeof v === 'number' ? v : '' }))}
                        min={0}
                        allowDecimal={false}
                        thousandSeparator=","
                    />
                ))}
                <Group justify="flex-end" mt="sm">
                    <Button variant="subtle" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => void submit()} loading={submitting} disabled={!name.trim()}>Save</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
